import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { StepItems } from 'src/app/shared/components/stepper/stepper.component';
import { CandidateSalary } from 'src/app/shared/models/candidate-salary.model';
import { OnboardingActions } from './index';

export const onboardingFeatureKey = 'onboarding';
export interface OnboardingState {
  steps: StepItems[];
  currentStep: number;
  jobTitles: { id: string, name: string, isCustomJobTitle: boolean }[];
  talent: {
    general: TalentGeneral;
    hardSkills: any;
    softSkills: any;
  }
  hm: {
    general: HmGeneral;
  }
  partner: {
    general: PartnerGeneral;
  }
}

export interface TalentGeneral {
  name: string;
  phoneCode: string;
  phone: string;
  jobTitle: string;
  jobTitles: string[];
  jobTitleLevel: string;
  salaryPeriod: string;
  salaryCurrency: string;
  salaryAmount: string;
  salary?: CandidateSalary | null;
  incognito: boolean;
}

export interface HardSkills {
  id: number;
  label: string,
  subSkills: {
    id: number;
    label: string;
    experience: number;
    years: number;
  }[] | null;
}

export interface HmGeneral {
  name: string;
  jobTitle: string;
  companyName: string;
  phoneCode: string;
  phone: string;
  companyLink: string;
  terms: boolean;
}

export interface PartnerGeneral {
  name: string;
  phone: string;
  phoneCode: string;
  country: string;
  city: string;
  terms: boolean;
}

const initialState: OnboardingState = {
  steps: [],
  currentStep: 0,
  jobTitles: null,
  talent: {
    general: null,
    hardSkills: null,
    softSkills: null
  },
  hm: {
    general: null,
  },
  partner: {
    general: null,
  }
}

const reducer = createReducer<OnboardingState>(
  initialState,
  on(OnboardingActions.SetSteps, (state, action) => ({ 
    ...state,
    steps: action.steps, 
    currentStep: action.currentStep 
  })),
  on(OnboardingActions.SetCurrentStep, (state, action) => ({ ...state, currentStep: action.currentStep })),
  on(OnboardingActions.StepValid, (state, action) => {
    const toReturn = {
      ...state,
      steps: state.steps.map(item => (item.key === action.key ? { ...item, isComplited: true } : item)),
      currentStep: state.steps.length > state.currentStep + 1 ? state.currentStep + 1 : state.currentStep,
      [action.section]: { 
        ...state[action.section],
        [action.key]: action.data ? { ...state[action.section][action.key], ...action.data } : state[action.section][action.key] 
      }
    }
    return toReturn;
  }),
  on(OnboardingActions.GetJobTitlesSuccess, (state, action) => ({ ...state, jobTitles: action.jobTitles })),
  on(OnboardingActions.GetGeneralInfoSuccess, (state, action) => {
    const toReturn = {
      ...state,
      [action.role]: { 
        ...state[action.role],
        general: { ...state[action.role].general, ...action.payload }
      }
    }
    return toReturn;
  }),
  on(OnboardingActions.SetHardSkills, (state, action) => ({ 
    ...state,
    talent: {
      ...state.talent,
      hardSkills: action.skillsList
    }
  })),
  on(OnboardingActions.GetHardSkillsSuccess, (state, action) => ({ 
    ...state,
    talent: {
      ...state.talent,
      hardSkills: action.skillsList ? action.skillsList : state.talent.softSkills
    }
  })),
  on(OnboardingActions.SetSoftSkills, (state, action) => ({ 
    ...state,
    talent: {
      ...state.talent,
      softSkills: action.skillsList
    }
  })),
  on(OnboardingActions.GetSoftSkillsSuccess, (state, action) => ({ 
    ...state,
    talent: {
      ...state.talent,
      softSkills: action.skillsList ? action.skillsList : state.talent.softSkills
    }
  }))
);

export function onboardingReducer(state: OnboardingState | undefined, action: Action) {
  return reducer(state, action);
}

export const selectOnboardingState = createFeatureSelector<OnboardingState>(onboardingFeatureKey);

export const selectStepsState = createSelector(
  selectOnboardingState,
  (state) => state.steps
);

export const selectCurrentSteps = createSelector(
  selectOnboardingState,
  (state) => state.currentStep
);

export const selectGeneralInfo = (role: string) => createSelector(
  selectOnboardingState,
  (state) => state[role] && state[role].general ? state[role].general : null
);

export const selectTalentGeneral = createSelector(
  selectOnboardingState,
  (state) => state.talent.general
);

export const selectTalentHardSkills = createSelector(
  selectOnboardingState,
  (state) => state.talent.hardSkills
);

export const selectTalentSoftSkills = createSelector(
  selectOnboardingState,
  (state) => state.talent.softSkills
);

export const selectHmGeneral = createSelector(
  selectOnboardingState,
  (state) => state.hm.general
);

export const selectPartnerGeneral = createSelector(
  selectOnboardingState,
  (state) => state.partner.general
);

export const selectJobTitles = createSelector(
  selectOnboardingState,
  (state) => state.jobTitles
);

export const selectCurrentJobTitle = createSelector(
  selectOnboardingState,
  (state) => state?.talent?.general?.jobTitle ? state.talent.general.jobTitle : 'Loading'
);

export const selectCurrentJobTitleLevel = createSelector(
  selectOnboardingState,
  (state) => state?.talent?.general?.jobTitleLevel ? state.talent.general.jobTitleLevel : null
);
